import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Tabs,
  Tab,
  Chip,
  Alert,
  AlertTitle,
  IconButton,
  Tooltip,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Science as ResearchIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  QuestionAnswer as FAQIcon,
  ContentPaste as GapIcon,
  Code as CodeIcon,
  CheckCircle as CheckCircleIcon,
  DoNotDisturb as NotRelevantIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  VerifiedUser as VerifiedIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { schemaApiService, Schema } from '../../services/schemaApiService';
import { getGapTypeLabel, getGapTypeColor, getPriorityLabel, getPriorityColor } from '../../utils/schemaUtils';
import { validateSchemaForDeployment, cleanSchemaForDeployment } from '../../utils/schemaCleanup';
import { AdvancedSchemaEditor } from './AdvancedSchemaEditor';
import { FAQFilteringStats } from './FAQFilteringStats';

interface SchemaDetailsProps {
  schema: Schema;
  onClose: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`schema-detail-tabpanel-${index}`}
      aria-labelledby={`schema-detail-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ px: 3, py: 3 }}>{children}</Box>}
    </div>
  );
}

const SchemaDetails: React.FC<SchemaDetailsProps> = ({ schema, onClose }) => {
  const [tabValue, setTabValue] = useState(0);
  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState(schema.status);
  const [updating, setUpdating] = useState(false);
  const [completedGaps, setCompletedGaps] = useState<Set<number>>(new Set());
  const [researching, setResearching] = useState(false);
  const [researchStatus, setResearchStatus] = useState<any>(null);
  const [researchSources, setResearchSources] = useState<any[]>([]);
  const [markingNotRelevant, setMarkingNotRelevant] = useState<string | null>(null);
  const [paaQuestions, setPaaQuestions] = useState(schema.paa_questions || []);
  const [editMode, setEditMode] = useState(false);
  const [editedSchema, setEditedSchema] = useState(schema.schema_json);
  const [saving, setSaving] = useState(false);
  const [editedFields, setEditedFields] = useState<Set<string>>(new Set());
  const [verificationStatus, setVerificationStatus] = useState<Record<string, any>>(schema.verification_status || {});
  const [verifying, setVerifying] = useState(false);
  const [removePricing, setRemovePricing] = useState(false);
  const [showAdvancedEditor, setShowAdvancedEditor] = useState(false);
  const [originalSchema, setOriginalSchema] = useState(schema.schema_json);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCopySchema = async () => {
    const success = await schemaApiService.copySchemaToClipboard(schema, removePricing);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      setUpdating(true);
      const result = await schemaApiService.updateStatus(
        schema.id, 
        newStatus as any,
        'user' // You might want to get actual user info here
      );
      if (result.success) {
        setStatus(newStatus);
      }
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleResearch = async () => {
    try {
      setResearching(true);
      const result = await schemaApiService.triggerResearch(schema.id, true); // immediate research
      
      if (result.success) {
        if (result.fieldsUpdated && result.fieldsUpdated > 0) {
          // Research completed immediately
          alert(`Research completed! ${result.fieldsUpdated} fields updated. Completeness: ${result.completeness}%`);
          // Reload the schema to show updated data
          window.location.reload();
        } else if (result.queueId) {
          // Added to queue
          alert('Schema queued for research. This may take a few moments.');
          // Check status periodically
          checkResearchStatus();
        } else {
          alert('No fields needed research.');
        }
      }
    } catch (error) {
      console.error('Error triggering research:', error);
      alert('Failed to start research. Please try again.');
    } finally {
      setResearching(false);
    }
  };

  const checkResearchStatus = async () => {
    try {
      const statusResult = await schemaApiService.getResearchStatus(schema.id);
      setResearchStatus(statusResult.status);
      
      if (statusResult.sources && statusResult.sources.length > 0) {
        setResearchSources(statusResult.sources);
      }
    } catch (error) {
      console.error('Error checking research status:', error);
    }
  };

  // Load research sources on mount
  useEffect(() => {
    checkResearchStatus();
  }, [schema.id]);

  const handleMarkGapDone = (gapIndex: number) => {
    setCompletedGaps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(gapIndex)) {
        newSet.delete(gapIndex);
      } else {
        newSet.add(gapIndex);
      }
      return newSet;
    });
  };

  const handleMarkNotRelevant = async (questionId: string) => {
    try {
      setMarkingNotRelevant(questionId);
      const result = await schemaApiService.markFAQAsNotRelevant(parseInt(questionId));
      if (result.success) {
        // Remove the question from the local state instead of reloading
        setPaaQuestions(prevQuestions => 
          prevQuestions.filter(q => q.question_id !== questionId)
        );
        // No need to update parent schema object as we're using local state
      } else {
        alert('Failed to mark FAQ as not relevant. Please try again.');
      }
    } catch (error) {
      console.error('Error marking FAQ as not relevant:', error);
      alert('Failed to mark FAQ as not relevant. Please try again.');
    } finally {
      setMarkingNotRelevant(null);
    }
  };

  const handleEditToggle = () => {
    if (editMode) {
      // Cancel edit mode
      setEditedSchema(schema.schema_json);
      setEditedFields(new Set());
      setEditMode(false);
      setShowAdvancedEditor(false);
    } else {
      // Enter edit mode
      setEditMode(true);
      setOriginalSchema(schema.schema_json);
    }
  };

  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      const result = await schemaApiService.updateSchema(
        schema.id, 
        editedSchema,
        Array.from(editedFields)
      );
      if (result.success) {
        schema.schema_json = editedSchema;
        schema.manual_edits = result.data.manual_edits;
        setEditedFields(new Set());
        setEditMode(false);
        alert('Schema updated successfully!');
      } else {
        alert('Failed to save schema. Please try again.');
      }
    } catch (error) {
      console.error('Error saving schema:', error);
      alert('Failed to save schema. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (fieldPath: string, value: any) => {
    const newSchema = { ...editedSchema };
    const pathParts = fieldPath.split('.');
    let current = newSchema;
    
    for (let i = 0; i < pathParts.length - 1; i++) {
      if (!current[pathParts[i]]) {
        current[pathParts[i]] = {};
      }
      current = current[pathParts[i]];
    }
    
    if (value === undefined || value === null || value === '') {
      // Remove the field if empty
      delete current[pathParts[pathParts.length - 1]];
    } else {
      current[pathParts[pathParts.length - 1]] = value;
    }
    
    setEditedSchema(newSchema);
    
    // Track that this field was edited
    setEditedFields(prev => {
      const newSet = new Set(prev);
      newSet.add(fieldPath);
      return newSet;
    });
  };

  const handleResetField = (fieldPath: string) => {
    const pathParts = fieldPath.split('.');
    let originalValue = originalSchema;
    
    for (const part of pathParts) {
      if (originalValue && typeof originalValue === 'object') {
        originalValue = originalValue[part];
      } else {
        originalValue = undefined;
        break;
      }
    }
    
    handleFieldChange(fieldPath, originalValue);
    
    // Remove from edited fields
    setEditedFields(prev => {
      const newSet = new Set(prev);
      newSet.delete(fieldPath);
      return newSet;
    });
  };

  const handleVerifySchema = async () => {
    try {
      setVerifying(true);
      const result = await schemaApiService.verifySchema(schema.id);
      if (result.success) {
        setVerificationStatus(result.verificationStatus);
        schema.verification_status = result.verificationStatus;
        alert('Verification completed!');
      }
    } catch (error) {
      console.error('Error verifying schema:', error);
      alert('Failed to verify schema. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const getVerificationIcon = (fieldPath: string) => {
    const status = verificationStatus[fieldPath];
    if (!status) return null;

    switch (status.status) {
      case 'verified':
        return (
          <Tooltip title={`Verified (${Math.round(status.confidence * 100)}% confidence) - ${status.verified_by || 'system'}`}>
            <VerifiedIcon color="success" fontSize="small" />
          </Tooltip>
        );
      case 'warning':
        return (
          <Tooltip title={status.warning}>
            <WarningIcon color="warning" fontSize="small" />
          </Tooltip>
        );
      case 'error':
        return (
          <Tooltip title={status.error}>
            <ErrorIcon color="error" fontSize="small" />
          </Tooltip>
        );
      case 'unverified':
        return (
          <Tooltip title="Needs manual verification">
            <InfoIcon color="info" fontSize="small" />
          </Tooltip>
        );
      default:
        return null;
    }
  };

  const getValidationIssues = () => {
    const schemaString = JSON.stringify(schema.schema_json, null, 2);
    const issues = [];
    
    // Find fields that need research
    const needsResearchMatches = schemaString.match(/"([^"]+)":\s*"[^"]*_needsResearch"/g);
    if (needsResearchMatches) {
      const fields = needsResearchMatches.map(match => {
        const fieldMatch = match.match(/"([^"]+)":/);
        return fieldMatch ? fieldMatch[1] : 'unknown field';
      });
      issues.push(`Fields needing research: ${fields.join(', ')}`);
    }
    
    // Find fields with low confidence
    const lowConfidenceMatches = schemaString.match(/"([^"]+)":\s*"[^"]*_confidence:\s*low"/g);
    if (lowConfidenceMatches) {
      const fields = lowConfidenceMatches.map(match => {
        const fieldMatch = match.match(/"([^"]+)":/);
        return fieldMatch ? fieldMatch[1] : 'unknown field';
      });
      issues.push(`Fields with low confidence: ${fields.join(', ')}`);
    }
    
    // Check completeness score
    if (schema.completeness_score && schema.completeness_score < 100) {
      issues.push(`Completeness score is ${schema.completeness_score}% (below 100%)`);
    }
    
    return issues;
  };

  const validationIssues = getValidationIssues();
  
  // Check schema structure validation
  const schemaValidation = validateSchemaForDeployment(schema.schema_json);
  const cleanedSchema = cleanSchemaForDeployment(schema.schema_json);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Schema Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {schema.url
              .replace(/^https?:\/\//, '')
              .replace(/^www_/, 'www.')
              .replace(/_co_uk_/, '.co.uk/')
              .replace(/_/g, '-')
              .replace(/-$/, '/')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              onChange={(e) => handleStatusChange(e.target.value)}
              label="Status"
              disabled={updating}
            >
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="deployed">Deployed</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
          </FormControl>
          {editMode ? (
            <>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveEdit}
                disabled={saving}
                color="success"
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleEditToggle}
                disabled={saving}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEditToggle}
              >
                Edit
              </Button>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={removePricing}
                    onChange={(e) => setRemovePricing(e.target.checked)}
                    size="small"
                  />
                }
                label="Remove pricing"
                sx={{ mr: 1 }}
              />
              <Tooltip title={copied ? "Copied!" : "Copy Schema"}>
                <Button
                  variant="outlined"
                  startIcon={copied ? <CheckIcon /> : <CopyIcon />}
                  onClick={handleCopySchema}
                  color={copied ? "success" : "primary"}
                >
                  Copy Schema
                </Button>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<ResearchIcon />}
                onClick={handleResearch}
                disabled={schema.completeness_score === 100 || researching}
              >
                {researching ? 'Researching...' : 'Research'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleVerifySchema}
                disabled={verifying}
                color="info"
              >
                {verifying ? 'Verifying...' : 'Verify'}
              </Button>
            </>
          )}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Validation Alerts */}
      {!schemaValidation.valid && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Schema Structure Issues (Will Fail Google Validation)</AlertTitle>
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
            {schemaValidation.errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
          <Typography variant="body2" sx={{ mt: 1 }}>
            These issues will be automatically fixed when copying the schema.
          </Typography>
        </Alert>
      )}
      
      {validationIssues.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Schema Quality Issues</AlertTitle>
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
            {validationIssues.map((issue, index) => (
              <li key={index}>{issue}</li>
            ))}
          </ul>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Use the Research button to enhance the data with accurate information.
          </Typography>
        </Alert>
      )}

      {/* Info Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 2.4 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="primary">
              {schema.completeness_score || 0}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Completeness Score
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 2.4 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="success.main">
              {paaQuestions.filter(q => q.answer_text && q.answer_text.trim() !== '').length}/{paaQuestions.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              FAQs Answered
            </Typography>
            {paaQuestions.filter(q => q.answer_text && q.answer_text.trim() !== '').length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" display="block">
                  {paaQuestions.filter(q => q.answer_source === 'schema').length} from schema
                </Typography>
                <Typography variant="caption" display="block">
                  {paaQuestions.filter(q => q.answer_source === 'content').length} from content
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 2.4 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="warning.main">
              {schema.content_gaps?.length || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Content Gaps
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 2.4 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            {(() => {
              const verifiedCount = Object.values(verificationStatus).filter(v => v.status === 'verified').length;
              const totalFields = Object.keys(verificationStatus).length;
              const percentage = totalFields > 0 ? Math.round((verifiedCount / totalFields) * 100) : 0;
              return (
                <>
                  <Typography variant="h4" color={percentage === 100 ? "success.main" : percentage >= 80 ? "info.main" : "warning.main"}>
                    {percentage}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Verified ({verifiedCount}/{totalFields})
                  </Typography>
                </>
              );
            })()}
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 2.4 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Chip 
              label={schema.content_type || 'Unknown'} 
              color="primary"
              sx={{ mt: 1 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Content Type
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Edit Form */}
      {editMode && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {showAdvancedEditor ? 'Advanced Schema Editor' : 'Edit Common Fields'}
            </Typography>
            <Button
              variant="text"
              color="primary"
              onClick={() => setShowAdvancedEditor(!showAdvancedEditor)}
            >
              {showAdvancedEditor ? 'Show Simple Editor' : 'Show All Fields'}
            </Button>
          </Box>
          
          {showAdvancedEditor ? (
            <AdvancedSchemaEditor
              schema={editedSchema}
              manualEdits={schema.manual_edits}
              verificationStatus={verificationStatus}
              onChange={handleFieldChange}
              onResetField={handleResetField}
            />
          ) : (
            <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={`Name ${schema.manual_edits?.name ? '✏️' : ''}`}
                value={editedSchema.name || ''}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                margin="normal"
                helperText={schema.manual_edits?.name ? `Manually edited on ${new Date(schema.manual_edits.name.edited_at).toLocaleDateString()}` : null}
                InputProps={{
                  endAdornment: getVerificationIcon('name')
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={`Price ${schema.manual_edits?.['offers.price'] ? '💰' : ''}`}
                value={editedSchema.offers?.price || ''}
                onChange={(e) => handleFieldChange('offers.price', e.target.value)}
                margin="normal"
                helperText={schema.manual_edits?.['offers.price'] ? `Manually edited • Original AI suggestion` : 'e.g., 24.30'}
                InputProps={{
                  endAdornment: getVerificationIcon('offers.price')
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={`Phone ${schema.manual_edits?.telephone ? '✏️' : ''}`}
                value={editedSchema.telephone || ''}
                onChange={(e) => handleFieldChange('telephone', e.target.value)}
                margin="normal"
                helperText={schema.manual_edits?.telephone ? 'Manually verified' : null}
                InputProps={{
                  endAdornment: getVerificationIcon('telephone')
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={`Opening Hours ${schema.manual_edits?.['openingHours.0'] ? '🕐' : ''}`}
                value={editedSchema.openingHours?.[0] || ''}
                onChange={(e) => handleFieldChange('openingHours.0', e.target.value)}
                margin="normal"
                helperText="e.g., Mo-Su 09:00-17:00"
                InputProps={{
                  endAdornment: getVerificationIcon('openingHours.0')
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Street Address"
                value={editedSchema.address?.streetAddress || ''}
                onChange={(e) => handleFieldChange('address.streetAddress', e.target.value)}
                margin="normal"
                InputProps={{
                  endAdornment: getVerificationIcon('address.streetAddress')
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={`Description ${schema.manual_edits?.description ? '✏️' : ''}`}
                value={editedSchema.description || ''}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Postal Code"
                value={editedSchema.address?.postalCode || ''}
                onChange={(e) => handleFieldChange('address.postalCode', e.target.value)}
                margin="normal"
                InputProps={{
                  endAdornment: getVerificationIcon('address.postalCode')
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Price Range"
                value={editedSchema.priceRange || ''}
                onChange={(e) => handleFieldChange('priceRange', e.target.value)}
                margin="normal"
                helperText="e.g., $ or $$ or $$$"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Latitude"
                value={editedSchema.geo?.latitude || ''}
                onChange={(e) => handleFieldChange('geo.latitude', e.target.value)}
                margin="normal"
                type="number"
                InputProps={{
                  endAdornment: getVerificationIcon('geo')
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Longitude"
                value={editedSchema.geo?.longitude || ''}
                onChange={(e) => handleFieldChange('geo.longitude', e.target.value)}
                margin="normal"
                type="number"
                InputProps={{
                  endAdornment: getVerificationIcon('geo')
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">
                <Typography variant="body2">
                  💡 Fields with icons (✏️ 💰 🕐) have been manually edited and won't be overwritten by AI.
                  Click "Show All Fields" to access advanced editing options for all schema properties.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
          )}
        </Paper>
      )}

      {/* FAQ Filtering Stats */}
      {schema.faq_filtering_savings && (
        <FAQFilteringStats stats={schema.faq_filtering_savings} />
      )}

      {/* Tabs */}
      <Paper>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Schema JSON" icon={<CodeIcon />} iconPosition="start" />
          <Tab label="Answered FAQs" icon={<CheckCircleIcon />} iconPosition="start" />
          <Tab label="Content Gaps & Unanswered FAQs" icon={<WarningIcon />} iconPosition="start" />
          {researchSources.length > 0 && (
            <Tab label="Research Sources" icon={<ResearchIcon />} iconPosition="start" />
          )}
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Box>
            {!schemaValidation.valid && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  The schema below shows the internal version with tracking fields. 
                  When you copy the schema, it will be automatically cleaned for deployment.
                </Typography>
              </Alert>
            )}
            <Typography variant="subtitle2" gutterBottom>
              {editMode ? 'Edit Schema JSON' : (schemaValidation.valid ? 'Schema JSON (Ready for Deployment)' : 'Schema JSON (Internal Version)')}
            </Typography>
            {editMode ? (
              <TextField
                fullWidth
                multiline
                value={JSON.stringify(editedSchema, null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    setEditedSchema(parsed);
                  } catch (err) {
                    // Invalid JSON, just update the text
                  }
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    fontFamily: 'monospace',
                    fontSize: '0.875rem'
                  }
                }}
              />
            ) : (
              <Box sx={{ 
                maxHeight: 600, 
                overflow: 'auto',
                backgroundColor: '#f5f5f5',
                p: 2,
                borderRadius: 1,
                mb: schemaValidation.valid ? 0 : 2
              }}>
                <pre style={{ 
                  margin: 0, 
                  fontSize: '0.875rem',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {JSON.stringify(schema.schema_json, null, 2)}
                </pre>
              </Box>
            )}
            
            {!schemaValidation.valid && (
              <>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Cleaned Schema (What will be copied)
                </Typography>
                <Box sx={{ 
                  maxHeight: 400, 
                  overflow: 'auto',
                  backgroundColor: '#e8f5e9',
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid #4caf50'
                }}>
                  <pre style={{ 
                    margin: 0, 
                    fontSize: '0.875rem',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {JSON.stringify(cleanedSchema, null, 2)}
                  </pre>
                </Box>
              </>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {paaQuestions && paaQuestions.filter(q => q.answer_text && q.answer_text.trim() !== '').length > 0 ? (
            <List>
              {paaQuestions
                .filter(q => q.answer_text && q.answer_text.trim() !== '')
                .map((faq: any, index: number) => (
                <React.Fragment key={faq.question_id || index}>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography>{faq.question_text}</Typography>
                          <Chip label="Answered" size="small" color="success" />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {faq.answer_text}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            {faq.answer_source === 'schema' && (
                              <Chip 
                                label="From schema" 
                                size="small" 
                                variant="outlined"
                                color="primary"
                              />
                            )}
                            {faq.answer_source === 'content' && (
                              <Chip 
                                label="From content" 
                                size="small" 
                                variant="outlined"
                                color="success"
                              />
                            )}
                            {faq.answer_source === 'research' && (
                              <Chip 
                                label="From research" 
                                size="small" 
                                variant="outlined"
                                color="info"
                              />
                            )}
                            {faq.answer_confidence && (
                              <Chip 
                                label={`Confidence: ${Math.round(faq.answer_confidence * 100)}%`} 
                                size="small" 
                                color={faq.answer_confidence > 0.8 ? "success" : "warning"}
                              />
                            )}
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                  {paaQuestions && index < paaQuestions.filter(q => q.answer_text && q.answer_text.trim() !== '').length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
              No FAQs have been answered yet
            </Typography>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* Show unanswered PAA questions first */}
          {paaQuestions && paaQuestions.filter(q => !q.answer_text || q.answer_text.trim() === '').length > 0 && (
            <>
              <Typography variant="h6" gutterBottom sx={{ px: 3, pt: 2 }}>
                Unanswered FAQs ({paaQuestions.filter(q => !q.answer_text || q.answer_text.trim() === '').length})
              </Typography>
              <List>
                {paaQuestions
                  .filter(q => !q.answer_text || q.answer_text.trim() === '')
                  .map((faq: any, index: number) => (
                  <React.Fragment key={faq.question_id || index}>
                    <ListItem>
                      <ListItemIcon>
                        <FAQIcon color="error" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography>{faq.question_text}</Typography>
                            <Chip label="Unanswered FAQ" size="small" color="error" />
                          </Box>
                        }
                        secondary={null}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        onClick={() => handleMarkNotRelevant(faq.question_id)}
                        disabled={markingNotRelevant === faq.question_id}
                        startIcon={<NotRelevantIcon />}
                        sx={{ ml: 1 }}
                      >
                        {markingNotRelevant === faq.question_id ? 'Marking...' : 'Not Relevant'}
                      </Button>
                    </ListItem>
                    {paaQuestions && index < paaQuestions.filter(q => !q.answer_text || q.answer_text.trim() === '').length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
              {schema.content_gaps && schema.content_gaps.length > 0 && (
                <Divider sx={{ my: 3 }} />
              )}
            </>
          )}
          
          {/* Show content gaps */}
          {schema.content_gaps && schema.content_gaps.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom sx={{ px: 3, pt: 2 }}>
                Content Gaps ({schema.content_gaps.length})
              </Typography>
              <List>
                {schema.content_gaps.map((gap: any, index: number) => {
                  const isCompleted = completedGaps.has(index);
                  return (
                    <React.Fragment key={gap.id || index}>
                      <ListItem
                        sx={{
                          opacity: isCompleted ? 0.5 : 1,
                          textDecoration: isCompleted ? 'line-through' : 'none'
                        }}
                      >
                        <ListItemIcon>
                          {isCompleted ? (
                            <CheckCircleIcon color="success" />
                          ) : (
                            <WarningIcon color={getGapTypeColor(gap.gap_type)} />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box>
                              <Typography>{gap.description}</Typography>
                            </Box>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                              <Chip 
                                label={getGapTypeLabel(gap.gap_type)} 
                                size="small" 
                                variant="outlined"
                                color={getGapTypeColor(gap.gap_type)}
                              />
                              {gap.priority && (
                                <Chip 
                                  label={`Priority: ${getPriorityLabel(gap.priority)}`} 
                                  size="small" 
                                  color={getPriorityColor(gap.priority)}
                                />
                              )}
                              {gap.suggestedAction && (
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  {gap.suggestedAction}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        <Button
                          size="small"
                          variant={isCompleted ? "outlined" : "contained"}
                          color={isCompleted ? "success" : "primary"}
                          onClick={() => handleMarkGapDone(index)}
                          startIcon={isCompleted ? <CheckIcon /> : null}
                        >
                          {isCompleted ? 'Done' : 'Mark Done'}
                        </Button>
                      </ListItem>
                      {schema.content_gaps && index < schema.content_gaps.length - 1 && <Divider />}
                    </React.Fragment>
                  );
                })}
              </List>
            </>
          )}
          
          {/* Show message if no gaps or unanswered FAQs */}
          {(!paaQuestions || paaQuestions.filter(q => !q.answer_text || q.answer_text.trim() === '').length === 0) &&
           (!schema.content_gaps || schema.content_gaps.length === 0) && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
              No content gaps or unanswered FAQs
            </Typography>
          )}
        </TabPanel>

        {researchSources.length > 0 && (
          <TabPanel value={tabValue} index={3}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Research Sources
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                The following sources were used to enhance this schema:
              </Typography>
              <List>
                {researchSources.map((source: any, index: number) => (
                  <React.Fragment key={source.id || index}>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box>
                            <Typography variant="subtitle1">
                              {source.field_name.charAt(0).toUpperCase() + source.field_name.slice(1)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Original: {source.original_value || 'Empty'}
                            </Typography>
                            <Typography variant="body2" color="success.main">
                              Updated: {source.researched_value}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" display="block">
                              Source: {source.source_url && (source.source_url.startsWith('http://') || source.source_url.startsWith('https://')) ? (
                                <a href={source.source_url} target="_blank" rel="noopener noreferrer">
                                  {source.source_title || source.source_url}
                                </a>
                              ) : (
                                <span>{source.source_title || source.source_url || 'Claude knowledge base'}</span>
                              )}
                            </Typography>
                            {source.cited_text && (
                              <Typography variant="caption" display="block" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                                "{source.cited_text}"
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                              <Typography 
                                variant="caption" 
                                display="block"
                                sx={{ 
                                  color: source.confidence >= 0.9 ? 'success.main' : 
                                         source.confidence >= 0.8 ? 'info.main' : 
                                         source.confidence >= 0.7 ? 'warning.main' : 'error.main',
                                  fontWeight: 'medium'
                                }}
                              >
                                Confidence: {Math.round((source.confidence || 0.8) * 100)}%
                              </Typography>
                              {source.confidence_reason && (
                                <Tooltip title={source.confidence_reason} arrow>
                                  <InfoIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                                </Tooltip>
                              )}
                            </Box>
                            {source.confidence_reason && (
                              <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                                {source.confidence_reason}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < researchSources.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          </TabPanel>
        )}
      </Paper>
    </Box>
  );
};

export default SchemaDetails;